import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";

export const CHAT_HISTORY_KEY = "se_chatHistory";

const formatChatName = (timestamp) =>
  `Chat ${dayjs(timestamp).format("DD.MM.YYYY")} - ${dayjs(timestamp).format("HH:mm")}`;

export const getDisplayName = (accounts) => {
  const raw = accounts?.[0]?.name || "";
  // Parse "LastName, FirstName (ext)" → "FirstName LastName"
  const match = raw.match(/^([^,]+),\s*([^(]+?)(?:\s*\(.*\))?\s*$/);
  if (match) return `${match[2].trim()} ${match[1].trim()}`;
  return raw;
};

export const createWelcomeMessage = (timestamp = Date.now(), userName = "") => ({
  id: uuidv4(),
  sender: "ai",
  text: `Hello${userName ? ` ${userName}` : ""}, this is AInstein! How can I help you? 👋`,
  timestamp,
});

const seedChat = (userName) => {
  const now = Date.now();
  return [
    {
      id: now,
      name: formatChatName(now),
      isActive: true,
      messages: [createWelcomeMessage(now, userName)],
      createdAt: now,
      lastMessageAt: now,
    },
  ];
};

export const readChatHistory = (userName = "") => {
  try {
    const stored = JSON.parse(localStorage.getItem(CHAT_HISTORY_KEY) || "[]");
    if (!Array.isArray(stored) || stored.length === 0) return seedChat(userName);

    const normalizedChats = stored.map((chat) => {
      if (!chat?.name) {
        const ts = chat?.createdAt || Date.now();
        return { ...chat, name: formatChatName(ts) };
      }
      return chat;
    });

    const hasActive = normalizedChats.some((c) => c.isActive);
    if (!hasActive && normalizedChats.length > 0) normalizedChats[0].isActive = true;

    return normalizedChats;
  } catch {
    return seedChat(userName);
  }
};

export const writeChatHistory = (history) => {
  try {
    // Never persist an in-flight/streaming message — mirrors Chat.jsx's own
    // persistence effect, which only ever writes settled messages.
    const persistableHistory = history.map((chat) => ({
      ...chat,
      messages: (chat.messages || []).filter((msg) => !msg?.isLoading),
    }));
    localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(persistableHistory));
  } catch {
    // Ignore storage write errors (private mode/quota).
  }
};

export const setActiveChatInHistory = (chatId, userName = "") => {
  const history = readChatHistory(userName);
  const next = history.map((c) => ({ ...c, isActive: c.id === chatId }));
  writeChatHistory(next);
  return next;
};

export const addNewChatToHistory = (userName = "") => {
  const history = readChatHistory(userName);
  const now = Date.now();
  const newChat = {
    id: now,
    name: formatChatName(now),
    isActive: true,
    messages: [createWelcomeMessage(now, userName)],
    createdAt: now,
    lastMessageAt: now,
  };

  const next = [newChat, ...history.map((chat) => ({ ...chat, isActive: false }))];
  writeChatHistory(next);
  return next;
};

export const removeChatFromHistory = (chatId, userName = "") => {
  const history = readChatHistory(userName);
  const remaining = history.filter((c) => c.id !== chatId);

  if (remaining.length === 0) {
    const next = seedChat(userName);
    writeChatHistory(next);
    return next;
  }

  const next = remaining.map((c, i) => ({ ...c, isActive: i === 0 }));
  writeChatHistory(next);
  return next;
};
